import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { XtreamContent } from '../api/xtreamAPI';
import { FavoriteSyncInput, useWatchSync } from '../hooks/useWatchSync';
import { loadProfileStorage, migrateAllProfileLists, profileStorageKey } from '../utils/profileStorage';

export type FavoriteType = 'live' | 'movies' | 'series';

export interface FavoriteItem {
  id: string;
  type: FavoriteType;
  name: string;
  icon?: string;
  categoryId?: string;
  streamId?: string;
  seriesId?: string;
  containerExtension?: string;
  /** When true, this item was favorited from a kids context (Kids page or kids live channel). */
  isKids?: boolean;
  updatedAt: number;
}

interface FavoritesContextType {
  items: FavoriteItem[];
  addFavorite: (item: FavoriteItem) => void;
  removeFavorite: (id: string, type: FavoriteType) => void;
  toggleFavorite: (item: FavoriteItem) => boolean;
  isFavorite: (id: string, type: FavoriteType) => boolean;
  getFavoritesByType: (type: FavoriteType) => FavoriteItem[];
  getFavoriteContentsByType: (type: FavoriteType) => XtreamContent[];
  clearAllLocal: () => void;
  /** Re-fetch favorites from the server (stale-while-revalidate). No-op when canSync is false. */
  refreshFromServer: () => void;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

const STORAGE_PREFIX = 'ott_player_favorites:';

const safeLoad = (key: string): FavoriteItem[] => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item): item is FavoriteItem =>
        !!item && typeof item.id === 'string' && typeof item.type === 'string' && typeof item.name === 'string'
    );
  } catch {
    return [];
  }
};

const toKey = (id: string, type: FavoriteType) => `${type}:${id}`;

const favoriteToContent = (item: FavoriteItem): XtreamContent => ({
  stream_id: item.streamId || item.id,
  series_id: item.type === 'series' ? (item.seriesId || item.streamId || item.id) : undefined,
  name: item.name,
  icon: item.icon,
  category_id: item.categoryId,
  container_extension: item.containerExtension,
  stream_type: item.type === 'movies' ? 'movie' : item.type === 'series' ? 'series' : 'livestream',
});

const toSyncItem = (item: FavoriteItem): FavoriteSyncInput => ({
  id:                 item.id,
  type:               item.type,
  name:               item.name,
  icon:               item.icon,
  categoryId:         item.categoryId,
  streamId:           item.streamId,
  seriesId:           item.seriesId,
  containerExtension: item.containerExtension,
  isKids:             item.isKids,
});

export const FavoritesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authData } = useAuth();
  const { canSync, fetchRemote, pushFavorites, removeFavoriteRemote } = useWatchSync();
  const username = authData?.user.username;
  const storageKey = useMemo(
    () => profileStorageKey(STORAGE_PREFIX, authData?.profileId, username) || `${STORAGE_PREFIX}guest`,
    [authData?.profileId, username],
  );
  const legacyStorageKey = username ? `${STORAGE_PREFIX}${username}` : null;
  const [items, setItems] = useState<FavoriteItem[]>([]);

  // Load from localStorage when account changes
  useEffect(() => {
    if (authData?.profileId) {
      migrateAllProfileLists<FavoriteItem>(
        storageKey,
        STORAGE_PREFIX,
        (item) => toKey(item.id, item.type),
        (item) => item.updatedAt,
      );
    } else {
      loadProfileStorage(storageKey, legacyStorageKey);
    }
    setItems(safeLoad(storageKey));
  }, [authData?.profileId, legacyStorageKey, storageKey]);

  // On mount / account change: fetch remote and REPLACE local (backend is source of truth)
  const syncedRef = useRef<string | null>(null);
  useEffect(() => {
    const key = storageKey;
    if (!canSync || !key || syncedRef.current === key) return;
    syncedRef.current = key;
    fetchRemote().then((remote) => {
      if (!remote) return; // network failure — keep local
      const remoteItems: FavoriteItem[] = remote.favorites.map(f => ({
        id:                 f.itemId,
        type:               f.itemType,
        name:               f.name,
        icon:               f.icon ?? undefined,
        categoryId:         f.categoryId ?? undefined,
        streamId:           f.streamId ?? undefined,
        seriesId:           f.seriesId ?? undefined,
        containerExtension: f.containerExtension ?? undefined,
        isKids:             f.isKids,
        updatedAt:          f.updatedAt,
      }));
      setItems(remoteItems);
      try { localStorage.setItem(key, JSON.stringify(remoteItems)); } catch { /* ignore */ }
    }).catch(() => { /* network error — local data stays */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSync, storageKey]);

  // Persist to localStorage whenever items change
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(items));
    } catch {
      // noop
    }
  }, [items, storageKey]);

  const addFavorite = useCallback((item: FavoriteItem) => {
    const entry = { ...item, updatedAt: Date.now() };
    setItems((prev) => {
      const next = [entry, ...prev.filter((e) => toKey(e.id, e.type) !== toKey(item.id, item.type))];
      pushFavorites([toSyncItem(entry)]);
      return next;
    });
  }, [pushFavorites]);

  const removeFavorite = useCallback((id: string, type: FavoriteType) => {
    setItems((prev) => prev.filter((e) => toKey(e.id, e.type) !== toKey(id, type)));
    removeFavoriteRemote(type, id);
  }, [removeFavoriteRemote]);

  const toggleFavorite = useCallback(
    (item: FavoriteItem) => {
      const has = items.some((e) => toKey(e.id, e.type) === toKey(item.id, item.type));
      if (has) {
        removeFavorite(item.id, item.type);
        return false;
      }
      addFavorite(item);
      return true;
    },
    [addFavorite, items, removeFavorite]
  );

  const isFavorite = useCallback(
    (id: string, type: FavoriteType) =>
      items.some((e) => toKey(e.id, e.type) === toKey(id, type)),
    [items]
  );

  const getFavoritesByType = useCallback(
    (type: FavoriteType) =>
      items.filter((e) => e.type === type).sort((a, b) => b.updatedAt - a.updatedAt),
    [items]
  );

  const getFavoriteContentsByType = useCallback(
    (type: FavoriteType) => getFavoritesByType(type).map(favoriteToContent),
    [getFavoritesByType]
  );

  const clearAllLocal = useCallback(() => {
    setItems([]);
    try { localStorage.setItem(storageKey, JSON.stringify([])); } catch { /* ignore */ }
  }, [storageKey]);

  const refreshFromServer = useCallback(() => {
    if (!canSync) return;
    fetchRemote().then((remote) => {
      if (!remote) return;
      const remoteItems: FavoriteItem[] = remote.favorites.map(f => ({
        id:                 f.itemId,
        type:               f.itemType,
        name:               f.name,
        icon:               f.icon ?? undefined,
        categoryId:         f.categoryId ?? undefined,
        streamId:           f.streamId ?? undefined,
        seriesId:           f.seriesId ?? undefined,
        containerExtension: f.containerExtension ?? undefined,
        isKids:             f.isKids,
        updatedAt:          f.updatedAt,
      }));
      setItems(remoteItems);
      try { localStorage.setItem(storageKey, JSON.stringify(remoteItems)); } catch { /* ignore */ }
    }).catch(() => { /* network error — keep local */ });
  }, [canSync, fetchRemote, storageKey]);

  const value = useMemo<FavoritesContextType>(
    () => ({ items, addFavorite, removeFavorite, toggleFavorite, isFavorite, getFavoritesByType, getFavoriteContentsByType, clearAllLocal, refreshFromServer }),
    [items, addFavorite, removeFavorite, toggleFavorite, isFavorite, getFavoritesByType, getFavoriteContentsByType, clearAllLocal, refreshFromServer]
  );

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
};

export const useFavoritesContext = (): FavoritesContextType => {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error('useFavoritesContext must be used within FavoritesProvider');
  return ctx;
};
